import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Gauge, Wrench, Headset, IndianRupee, Leaf, BadgeCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TRUST_CHIPS } from "@/data/content";
import { useQuote } from "@/context/QuoteContext";

const chipIcons = [ShieldCheck, Gauge, Wrench, Headset];

const Hero = () => {
  const { openQuote } = useQuote();

  return (
    <section id="home" className="relative overflow-hidden hero-radial pt-[104px] md:pt-[120px] pb-16 md:pb-24">
      <div className="max-shell mx-auto px-4 md:px-8 lg:px-12">
        <div className="grid lg:grid-cols-[45%_55%] gap-10 lg:gap-8 items-center">
          {/* Left */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }}
          >
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-brand-green bg-brand-green/10 rounded-full px-4 py-1.5">
              <Leaf className="h-3.5 w-3.5" /> Clean Energy. Smarter Savings.
            </span>
            <h1 className="mt-5 text-[32px] md:text-[42px] lg:text-[64px] leading-[1.08] font-bold text-brand-navy text-balance">
              Power Your Property With{" "}
              <span className="text-brand-green">Smarter Solar</span> Energy
            </h1>
            <p className="mt-5 text-base md:text-lg leading-[1.8] text-slate-500 max-w-xl">
              End-to-end solar solutions for homes, businesses and industries — engineered, installed and supported by experts.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => openQuote("hero")}
                data-testid="hero-cta-primary"
                className="h-14 rounded-full bg-brand-yellow text-brand-navy font-semibold text-base px-7 hover:bg-[#f0bd1f] hover:-translate-y-0.5 transition-transform shadow-soft group"
              >
                Get Free Solar Assessment
                <ArrowRight className="ml-1 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                asChild
                variant="outline"
                data-testid="hero-cta-secondary"
                className="h-14 rounded-full border-[1.5px] border-brand-green text-brand-navy font-semibold text-base px-7 hover:bg-brand-green/5 transition-colors"
              >
                <a href="#solutions">View Solutions</a>
              </Button>
            </div>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {TRUST_CHIPS.map((c, i) => {
                const Icon = chipIcons[i];
                return (
                  <div key={c} className="flex flex-col items-center text-center gap-2 rounded-2xl bg-white/70 border border-slate-100 px-2 py-4 shadow-soft" data-testid={`trust-chip-${i}`}>
                    <span className="grid place-items-center h-9 w-9 rounded-full bg-brand-green/10 text-brand-green">
                      <Icon className="h-4.5 w-4.5" />
                    </span>
                    <span className="text-[11px] font-semibold leading-tight text-brand-navy">{c}</span>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Right */}
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
            className="relative"
          >
            <div className="relative rounded-[28px] overflow-hidden shadow-glass aspect-[4/3] lg:aspect-[16/12]">
              <img
                src="https://images.unsplash.com/photo-1771479755134-9c1e3143c110?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
                alt="Modern rooftop solar panel installation on a home overlooking a city skyline at sunset"
                className="h-full w-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/25 via-transparent to-transparent" />
            </div>

          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
